import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import { RiCloseLine, RiDeleteBinLine } from "react-icons/ri";
import { FiPlay } from "react-icons/fi";
import type { Job } from "../types/job";
import { jobErrorText } from "../lib/jobError";
import api from "../lib/api";

interface PodcastAudioPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss?: (jobId: string) => Promise<void>;
  showHistory?: boolean;
}

export default function PodcastAudioPreviewModal({
  isOpen,
  onClose,
  job,
  onApprove,
  onDecline,
  onDismiss,
  showHistory = false,
}: PodcastAudioPreviewModalProps) {
  const { t, i18n } = useTranslation();
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [currentPlayingAudio, setCurrentPlayingAudio] = useState<string | null>(null);
  const [currentJob, setCurrentJob] = useState<Job>(job);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const getJobDisplayName = (name: string | null | undefined) => {
    if (!name) return t("modal.audioGeneration");
    const key = `jobNames.${name.toLowerCase().replace(/\s+/g, "_")}`;
    return i18n.exists(key) ? t(key) : name;
  };

  // ✅ Modal açıldığında job detaylarını API'den çek
  useEffect(() => {
    if (!isOpen || !job.id) return;

    const fetchJobDetails = async () => {
      try {
        setIsLoadingDetails(true);

        const response = await api.get<Job>(`/${job.id}`);

        setCurrentJob(response.data);
      } catch (error) {
        console.error(`[PodcastAudioPreviewModal] ❌ Failed to fetch job details:`, error);
        setCurrentJob(job);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchJobDetails();
  }, [isOpen, job.id]);

  // Esc closes the modal (same callback as the X). Only attached while mounted.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await onApprove(currentJob);
      onClose();
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await onDecline(currentJob.id);
      onClose();
    } finally {
      setIsDeclining(false);
    }
  };

  const handleDismiss = async () => {
    if (onDismiss) {
      await onDismiss(currentJob.id);
      onClose();
    }
  };

  // Try to find podcast audio data
  const podcastAudioData =
    currentJob.result?.audioResults ||
    currentJob.result?.podcastAudio ||
    currentJob.result?.sections ||
    currentJob.result ||
    (currentJob as any).audioResults ||
    (currentJob as any).podcastAudio ||
    (currentJob as any).sections ||
    (currentJob as any).data?.audioResults ||
    (currentJob as any).data?.podcastAudio ||
    (currentJob as any).data?.sections;

  const podcastAudioSections = Array.isArray(podcastAudioData) ? podcastAudioData : [];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] max-h-[85vh] rounded-3xl border border-white/20 bg-white/95 backdrop-blur-xl shadow-2xl z-70 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5 pointer-events-none" />

            <div className="relative">
              <div className="flex items-center justify-between p-5 border-b border-black/5">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {getJobDisplayName(currentJob.name)}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t("jobCard.scene")} {currentJob.sceneIndex ?? "-"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {onDismiss && !showHistory && (
                    <button
                      onClick={handleDismiss}
                      className="p-2 hover:bg-red-50 rounded-xl transition-colors"
                      title={t("actions.dismiss")}
                    >
                      <RiDeleteBinLine className="w-5 h-5 text-gray-600 hover:text-red-600" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-black/5 rounded-xl transition-colors"
                  >
                    <RiCloseLine className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="p-5 max-h-[calc(85vh-140px)] overflow-y-auto">
                {isLoadingDetails ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                      <svg
                        className="w-8 h-8 text-purple-600 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">{t("podcast.loadingDetails")}</p>
                    <p className="text-sm text-gray-500 mt-2">{t("podcast.pleaseWait")}</p>
                  </div>
                ) : currentJob.status === "failed" ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                      <svg
                        className="w-8 h-8 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-red-600 font-bold text-lg mb-2">{t("widget.failed")}</p>
                    <p className="text-sm text-gray-600 mb-4">{t("podcast.audioFailed")}</p>
                    {currentJob.error && (
                      <div className="mx-auto max-w-md bg-red-50 border border-red-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-red-800 mb-1">
                          {t("jobCard.error")}:
                        </p>
                        <p className="text-sm text-red-700 break-words">
                          {jobErrorText(currentJob, t)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : podcastAudioSections.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-50/80 to-fuchsia-50/80 border border-purple-100/50">
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <h3 className="text-sm font-semibold text-purple-900">
                        {t("podcast.audioReady")}
                      </h3>
                    </div>

                    <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2">
                      {podcastAudioSections.map((section: any, sectionIndex: number) => (
                        <div
                          key={sectionIndex}
                          className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/40 shadow-sm"
                        >
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200/50">
                            <span className="w-1 h-5 bg-gradient-to-b from-purple-500 to-fuchsia-500 rounded-full"></span>
                            <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wide">
                              {section.sectionName || `Section ${sectionIndex + 1}`}
                            </h4>
                          </div>
                          <div className="space-y-2.5">
                            {section.chunks?.map((chunk: any, chunkIndex: number) => (
                              <div
                                key={chunkIndex}
                                className="flex items-center gap-3 p-3 bg-white/50 rounded-xl hover:bg-white/80 transition-all group"
                              >
                                {/* Play Button */}
                                <button
                                  onClick={() => {
                                    const audioId = `${sectionIndex}-${chunkIndex}`;
                                    const audioElement = document.getElementById(
                                      audioId
                                    ) as HTMLAudioElement;
                                    if (audioElement) {
                                      if (currentPlayingAudio === audioId) {
                                        audioElement.pause();
                                        setCurrentPlayingAudio(null);
                                      } else {
                                        // Pause any other playing audio
                                        if (currentPlayingAudio) {
                                          const prevAudio = document.getElementById(
                                            currentPlayingAudio
                                          ) as HTMLAudioElement;
                                          if (prevAudio) prevAudio.pause();
                                        }
                                        audioElement.play();
                                        setCurrentPlayingAudio(audioId);
                                      }
                                    }
                                  }}
                                  className="shrink-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white rounded-xl hover:from-purple-600 hover:to-fuchsia-600 transition-all shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 active:scale-95"
                                  title={
                                    currentPlayingAudio === `${sectionIndex}-${chunkIndex}`
                                      ? "Pause"
                                      : "Play"
                                  }
                                >
                                  {currentPlayingAudio === `${sectionIndex}-${chunkIndex}` ? (
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : (
                                    <FiPlay className="w-5 h-5 ml-0.5" />
                                  )}
                                </button>

                                {/* Audio Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-800">
                                    Audio {chunkIndex + 1}
                                  </div>
                                  {chunk.duration > 0 && (
                                    <div className="text-xs text-gray-600 mt-0.5">
                                      Duration: {chunk.duration.toFixed(1)}s
                                    </div>
                                  )}
                                </div>

                                {/* Download Button */}
                                <a
                                  href={chunk.audioUrl}
                                  download={`${section.sectionName || "section"}-${chunkIndex + 1}.mp3`}
                                  className="shrink-0 px-3 py-2 text-xs font-semibold text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-all border border-purple-100/50 hover:shadow-sm"
                                  title={t("podcast.downloadAudio")}
                                >
                                  {t("podcast.download")}
                                </a>

                                {/* Hidden Audio Element */}
                                <audio
                                  id={`${sectionIndex}-${chunkIndex}`}
                                  src={chunk.audioUrl}
                                  onEnded={() => setCurrentPlayingAudio(null)}
                                  className="hidden"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                      <svg
                        className="w-8 h-8 text-purple-600 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">{t("podcast.generatingAudio")}</p>
                    <p className="text-sm text-gray-500 mt-2">{t("podcast.takesAMoment")}</p>
                  </div>
                )}

                {!isLoadingDetails && !showHistory && (
                  <div className="flex gap-3 pt-4 mt-4 border-t border-gray-200">
                    <button
                      onClick={handleDecline}
                      disabled={isDeclining || isApproving}
                      className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDeclining ? t("actions.declining") : t("actions.decline")}
                    </button>

                    {currentJob?.status === "completed" && (
                      <button
                        onClick={handleApprove}
                        disabled={isApproving || isDeclining}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/25"
                      >
                        {isApproving ? t("actions.approving") : t("actions.approve")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
